import type { Section, FormInstance, Card } from '../types';
import { evaluateConditions } from './conditions';
import { mergeFormValues } from './valueMerger';
import { hasCardCompletedInstances, getVisibleCards } from './cardUtils';

export type SectionStatus = 'TO DO' | 'In Progress' | 'Complete';

export type DictionaryT = (key: string, fallback?: string) => string;

function localizeStatus(status: SectionStatus, t?: DictionaryT): SectionStatus {
  // If you truly want localized strings, change SectionStatus to string.
  // If you want to keep these internal statuses stable, DO NOT localize here.
  // This helper is shown only if you intend to output localized labels from these functions.
  if (!t) return status;

  switch (status) {
    case 'TO DO':
      return t('ToDo', 'TO DO') as SectionStatus;
    case 'In Progress':
      return t('InProgress', 'In Progress') as SectionStatus;
    case 'Complete':
      return t('Complete', 'Complete') as SectionStatus;
    default:
      return status;
  }
}

/**
 * Determines the status of a legacy section (without cards)
 */
export function getLegacySectionStatus(
  section: Section,
  completedInstances: FormInstance[],
  currentSectionId: string | null,
  t?: DictionaryT
): SectionStatus {
  const isInProgress = currentSectionId === section.id;
  const hasCompletedInstances = completedInstances.length > 0;

  if (isInProgress && !hasCompletedInstances) {
    return localizeStatus('TO DO', t);
  }

  if (hasCompletedInstances) {
    return localizeStatus('Complete', t);
  }

  return localizeStatus('TO DO', t);
}

/**
 * Determines the status of a card section
 */
export function getCardSectionStatus(
  section: Section,
  cards: Card[],
  completedForms: Record<string, FormInstance[]>,
  currentSectionId: string | null,
  values: Record<string, unknown>,
  localStorageKey?: string,
  t?: DictionaryT
): SectionStatus {
  const isInProgress = currentSectionId === section.id;

  const mergedValues = mergeFormValues(values, completedForms, localStorageKey);
  const visibleCards = getVisibleCards(cards, mergedValues);

  if (visibleCards.length === 0) {
    return localizeStatus('TO DO', t);
  }

  const hasCardInProgress = visibleCards.some((card) => currentSectionId === card.id);

  if (isInProgress && hasCardInProgress) {
    return localizeStatus('TO DO', t);
  }

  const allCardsCompleted = visibleCards.every((card) =>
    hasCardCompletedInstances(card, completedForms, mergedValues)
  );

  if (allCardsCompleted) {
    return localizeStatus('Complete', t);
  }

  const someCardsCompleted = visibleCards.some((card) =>
    hasCardCompletedInstances(card, completedForms, mergedValues)
  );

  if (someCardsCompleted) {
    return localizeStatus('In Progress', t);
  }

  return localizeStatus('TO DO', t);
}

/**
 * Main function to get section status
 */
export function getSectionStatus(
  section: Section,
  completedForms: Record<string, FormInstance[]>,
  currentSectionId: string | null,
  values: Record<string, unknown>,
  localStorageKey?: string,
  getDictionaryValue?: DictionaryT
): SectionStatus {
  if (section.cards && section.cards.length > 0) {
    return getCardSectionStatus(
      section,
      section.cards,
      completedForms,
      currentSectionId,
      values,
      localStorageKey,
      getDictionaryValue
    );
  }

  const groupKey = section.groupKey || section.id;
  const completedInstances = completedForms[groupKey] || [];
  return getLegacySectionStatus(section, completedInstances, currentSectionId, getDictionaryValue);
}

/**
 * Checks if all visible sections are completed
 */
export function areAllSectionsCompleted(
  sections: Section[],
  completedForms: Record<string, FormInstance[]>,
  currentSectionId: string | null,
  values: Record<string, unknown>,
  localStorageKey?: string,
  getDictionaryValue?: DictionaryT
): boolean {
  const mergedValues = mergeFormValues(values, completedForms, localStorageKey);

  const visibleSections = sections.filter((section) =>
    evaluateConditions(section.showIf, mergedValues)
  );

  if (visibleSections.length === 0) return false;

  return visibleSections.every((section) => {
    const status = getSectionStatus(
      section,
      completedForms,
      currentSectionId,
      values,
      localStorageKey,
      getDictionaryValue
    );
    // NOTE: if you localize statuses to different strings, this comparison can break.
    return (
      status ===
      (getDictionaryValue
        ? (getDictionaryValue('status.complete', 'Complete') as SectionStatus)
        : 'Complete')
    );
  });
}
